import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { createSelector } from "reselect"
import moment from "moment-timezone"
import { Symbol } from "../../../../../constants"
import { formatAsPrice, formatDateTime } from "../../../../../utils/formatters"
import { getCurrentAgenda, getCurrentPath, getRecordEditId } from "../../../../router/selectors"
import { getCustomers, getLoyaltyProgramFormData } from "../../../../data/selectors"
import { getReportsStateObject } from "../../selectors"
import type { StateObject } from "../../../../redux/types"
import type { CustomerAnalyticsTableItem } from "../components/CustomerAnalyticsTable"
import type { CustomerNoteItem } from "../components/CustomerNotesComponent"
import type { CustomerCredentialFormItem, CustomerFormItem } from "../CustomerForm"
import type {
    Customer,
    CustomerLoyaltyProgram,
    CustomerLoyaltyProgramStatsItem,
    CustomerOrder,
    LoyaltyProgramData,
} from "../../../../data/types"
import type { CustomerTableDataItem } from "./CustomersTable"
import type { CustomerOrderTableItem } from "../components/CustomerOrdersTable"
import type { CustomerPagesObject, CustomersState } from "../../reducer"
import { getFilters } from "../../../../filters/selectors"
import type { QueryApiParameters } from "../../../../../utils/queryApi/queryApiRequest"
import { composeSearchAmountExpression, composeSearchExpressions } from "../../../../filters/utils"
import type { CustomerLoyaltyProgramFormData } from "../components/CustomerLoyaltyProgram"
import { MainMenuAgendaEnum } from "../../../../router/types"
import type { CustomerReportItem } from "../../../../../graphql/types"
import { formatPhoneNumber } from "@figurepos/fgr-lib-business-logic/lib/formatters/phone"

export const getCustomersState = (state: StateObject): CustomersState => getReportsStateObject(state).customers

export const getCustomersPages = (state: StateObject): CustomerPagesObject => getCustomersState(state).pages

export const getCustomersCurrentPage = (state: StateObject): number =>
    getFilters(state, "customers").pagination.currentPage

export const getCustomersByPageNumber = (state: StateObject, page: number): Maybe<CustomerReportItem[]> =>
    getCustomersPages(state)[page]

export const getCustomersInCurrentPage: (state: StateObject) => CustomerReportItem[] = createSelector(
    getCustomersPages,
    getCustomersCurrentPage,
    (pages: CustomerPagesObject, current: number) => pages[current] || []
)

export const getCustomersQueryApiParam = (state: StateObject): QueryApiParameters => {
    const filters = getFilters(state, "customers")
    const filter =
        filters.search && filters.search.value.length > 0 && filters.search.fields.length > 0
            ? composeSearchExpressions(filters.search.fields, filters.search.value)
                  .or(composeSearchAmountExpression("total_spend", filters.search.value))
                  .or(composeSearchAmountExpression("average_spend", filters.search.value))
            : null
    const offset = (filters.pagination.currentPage - 1) * filters.pagination.pageLimit
    return {
        filter: filter,
        sort: [filters.sort],
        limit: filters.pagination.pageLimit,
        offset: offset,
    }
}

export const isCustomerReportAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.CustomerReport

export const isCustomerFeedbackAgendaOpened = (state: StateObject): boolean => {
    const currentPath = getCurrentPath(state)

    if (currentPath) {
        const tab = currentPath.query && currentPath.query.tab
        return isCustomerReportAgendaOpened(state) && !!tab && tab === "feedback"
    }

    return false
}

export const isGoogleReviewsAgendaOpened = (state: StateObject): boolean => {
    const currentPath = getCurrentPath(state)

    if (currentPath) {
        const tab = currentPath.query && currentPath.query.tab
        return isCustomerReportAgendaOpened(state) && !!tab && tab === "google-reviews"
    }

    return false
}

export const shouldDownloadLocationsForGoogleReviews = (state: StateObject): boolean => {
    // we need locations ready when we open google reviews tab
    return isCustomerReportAgendaOpened(state)
}

export const getEditCustomerId = (state: StateObject) => getRecordEditId(state)

export const getEditCustomer = (state: StateObject): Maybe<CustomerReportItem> => {
    const customers = getCustomersInCurrentPage(state)
    return customers.find(c => c.id === getEditCustomerId(state))
}

export const getCustomerDetail = (state: StateObject): Maybe<Customer> =>
    getCustomers(state).find(c => c.id === getEditCustomerId(state))

export const getDataForCustomersReportTable = (state: StateObject): CustomerTableDataItem[] =>
    getCustomersInCurrentPage(state).map((c: CustomerReportItem) => {
        return {
            customerId: c.id,
            customerName: c.customerName || "",
            customerPersonalName: c.customerPersonalName || "",
            companyName: c.companyName || null,
            isCompany: c.isCompany,
            phoneNumber: c.phoneNumber ? formatPhoneNumber(c.phoneNumber) : null,
            email: c.email || null,
            totalSpend: c.totalSpend ? formatAsPrice(c.totalSpend) : "0",
            visits: c.numberOfVisits || 0,
            averageSpend: c.averageSpend ? formatAsPrice(c.averageSpend) : "0",
            lastVisit: c.lastVisitDatetime ? formatDateTime(new Date(c.lastVisitDatetime), "M/D/YYYY") : "",
        }
    })

export const getCustomerCredentials = (state: StateObject): Maybe<CustomerCredentialFormItem> => {
    const customer: Maybe<Customer> = getCustomerDetail(state)

    if (!customer) {
        return null
    }

    return {
        isCompany: customer.isCompany,
        companyEntityName: customer.companyName,
        positionAtCompany: customer.position,
        firstName: customer.firstName || "",
        surname: customer.surname || "",
        gender: customer.gender || null,
        birth: customer.dateOfBirth && moment(customer.dateOfBirth).format("YYYY-MM-DD"),
        phoneNumber: customer.phoneNumber,
        email: customer.email,
        addresses: customer.addresses,
    }
}

export const getCustomerFormData = (state: StateObject): Maybe<CustomerFormItem> => {
    const customer: Maybe<CustomerReportItem> = getEditCustomer(state)

    if (!customer) {
        return null
    }

    return {
        customerId: customer.id,
    }
}

export const getCustomerAnalyticsData = (state: StateObject): Maybe<CustomerAnalyticsTableItem> => {
    const customer: Maybe<CustomerReportItem> = getEditCustomer(state)
    const customerData: Maybe<Customer> = getCustomerDetail(state)

    if (!customer) {
        return null
    }

    return {
        totalSpend: customer.totalSpend ? formatAsPrice(customer.totalSpend) : Symbol.MDash,
        dateOfBirth:
            customerData && customerData.dateOfBirth ? moment(customerData.dateOfBirth).format("MM/DD") : Symbol.MDash,
        numberOfVisits: customer.numberOfVisits ? customer.numberOfVisits.toString() : Symbol.MDash,
        averageSpend: customer.averageSpend ? formatAsPrice(customer.averageSpend) : Symbol.MDash,
        last28days: customer.last28daysSpend ? formatAsPrice(customer.last28daysSpend) : Symbol.MDash,
        lastVisit: customer.lastVisitSpend ? formatAsPrice(customer.lastVisitSpend) : Symbol.MDash,
        lastTransaction: customer.lastTransactionSpend ? formatAsPrice(customer.lastTransactionSpend) : Symbol.MDash,
        lastVisitDateTime: customer.lastVisitDatetime
            ? formatDateTime(new Date(customer.lastVisitDatetime), "D MMM YYYY")
            : "",
        mostFrequent: customer.mostFrequent,
    }
}

export const getCustomerLoyaltyProgramFormData = (state: StateObject): Maybe<CustomerLoyaltyProgramFormData> => {
    const customerData: Maybe<Customer> = getCustomerDetail(state)
    const loyaltyProgram: Maybe<LoyaltyProgramData> = getLoyaltyProgramFormData(state)

    if (!customerData || !customerData.customerLoyaltyProgram) {
        return null
    }

    if (!loyaltyProgram || customerData.customerLoyaltyProgram.loyalty_program_id !== loyaltyProgram.id) {
        return null
    }

    const customerLoyalty: CustomerLoyaltyProgram = customerData.customerLoyaltyProgram
    const stats: Maybe<CustomerLoyaltyProgramStatsItem> = customerData.customerLoyaltyProgram.stats || null
    return {
        customerLoyaltyProgramId: customerLoyalty.id,
        customerId: customerData.id,
        loyaltyProgramId: customerLoyalty.loyalty_program_id,
        rewardBalance: stats ? stats.rewards_balance : null,
        redeemableReward: stats ? stats.redeemable : null,
        redeemableRewardToSpend: stats ? stats.spend_to_redeemable_reward : null,
        totalRewardsRedeemed: stats ? stats.total_rewards_redeemed : null,
        optInDate: customerLoyalty.opt_in_date,
        optOutDate: customerLoyalty.opt_out_date,
    }
}

export const getDataForCustomerOrdersTable = (state: StateObject): CustomerOrderTableItem[] =>
    getCustomersState(state).customerOrders.map((o: CustomerOrder) => ({
        orderId: o.id,
        orderNumber: o.orderNumber,
        assignment: o.assignment || "",
        createdTimestamp: o.createdTimestamp ? formatDateTime(new Date(o.createdTimestamp), "M/D/YYYY") : "",
        total: o.total || 0,
    }))

export const getCustomerNotesData = (state: StateObject): CustomerNoteItem[] => {
    const customer: Maybe<Customer> = getCustomerDetail(state)

    if (!customer) {
        return []
    }

    return customer.notes.map(n => ({
        id: n.id,
        note: n.note || "",
        userName: n.userName || "",
        timeStamp: n.timestamp ? formatDateTime(new Date(n.timestamp), "D MMM YYYY, h:mm a") : "",
    }))
}
